
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, ExternalLink, RefreshCw, AlertCircle, Plus, X } from 'lucide-react';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Gauge } from '@/components/pagespeed/Gauge';
import { MetricCard } from '@/components/pagespeed/MetricCard';
import { usePageSpeedScan } from '@/hooks/usePageSpeedScan';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

// Define TypeScript interfaces for the PageSpeed result data
interface PageSpeedAudit {
  id: string;
  title: string;
  description: string;
  score: number;
  displayValue: string;
}

interface PageSpeedCategory {
  id: string;
  title: string;
  description: string;
  score: number;
}

interface PageSpeedResult {
  url: string;
  lighthouseResult?: {
    finalUrl: string;
    categories: {
      [key: string]: PageSpeedCategory;
    };
    audits: {
      [key: string]: PageSpeedAudit;
    };
  };
}

export default function PageSpeed() {
  const [urls, setUrls] = useState<string[]>(['']);
  const [activeTab, setActiveTab] = useState<'mobile' | 'desktop'>('mobile');
  const { toast } = useToast();
  
  const { 
    scanMultiple, 
    results,
    isLoading, 
    error 
  } = usePageSpeedScan();

  // Handle adding new URL input
  const handleAddUrl = () => {
    setUrls([...urls, '']);
  };

  // Handle removing URL input
  const handleRemoveUrl = (index: number) => {
    if (urls.length > 1) {
      const newUrls = [...urls];
      newUrls.splice(index, 1);
      setUrls(newUrls);
    }
  };

  // Handle URL input change
  const handleUrlChange = (index: number, value: string) => {
    const newUrls = [...urls];
    newUrls[index] = value;
    setUrls(newUrls);
  };

  const handleScan = async () => {
    // Filter out empty URLs
    const urlsToScan = urls.filter(url => url.trim() !== '');
    
    if (urlsToScan.length === 0) {
      toast({
        title: "URL Required",
        description: "Please enter at least one valid URL to analyze",
        variant: "destructive"
      });
      return;
    }

    try {
      // Normalize URLs if needed
      const normalizedUrls = urlsToScan.map(url => {
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          return `https://${url}`;
        }
        return url;
      });
      
      await scanMultiple(normalizedUrls, activeTab);
    } catch (err) {
      console.error('Scan error:', err);
      toast({
        title: "Scan Failed",
        description: "An error occurred during the page speed scan",
        variant: "destructive"
      });
    }
  };

  return (
    <DashboardLayout>
      <motion.div
        className="space-y-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <motion.h1 
          className="text-3xl font-semibold"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          PageSpeed Insights
        </motion.h1>
        
        {/* URL Input Section */}
        <Card>
          <CardHeader>
            <CardTitle>Analyze Web Performance</CardTitle>
            <CardDescription>
              Enter URLs to analyze their performance metrics using Google PageSpeed Insights
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-4">
              {urls.map((url, index) => (
                <div key={index} className="flex flex-col gap-4 md:flex-row">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="url"
                        placeholder="Enter website URL (e.g., https://example.com)"
                        className="pl-9"
                        value={url}
                        onChange={(e) => handleUrlChange(index, e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleScan()}
                      />
                    </div>
                  </div>
                  
                  {index === 0 ? (
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={handleAddUrl}
                      className="h-10 w-10 md:w-10"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => handleRemoveUrl(index)}
                      className="h-10 w-10 md:w-10"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              
              <div className="flex flex-col gap-4 md:flex-row mt-4">
                <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'mobile' | 'desktop')} className="w-full md:w-40">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="mobile">Mobile</TabsTrigger>
                    <TabsTrigger value="desktop">Desktop</TabsTrigger>
                  </TabsList>
                </Tabs>
                
                <Button 
                  onClick={handleScan} 
                  disabled={isLoading || urls.every(url => !url.trim())} 
                  className="md:w-32"
                >
                  {isLoading ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
                  {isLoading ? "Scanning..." : "Analyze"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Results Section */}
        {error && (
          <Card className="border-destructive">
            <CardHeader className="text-destructive">
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Error
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p>Failed to analyze the URL. Please check the URL and try again.</p>
              <p className="text-sm text-muted-foreground mt-2">{error.message}</p>
            </CardContent>
          </Card>
        )}
        
        {results && results.length > 0 && !error && (
          <div className="space-y-4">
            <Accordion type="single" collapsible className="w-full">
              {results.map((result: PageSpeedResult, index: number) => (
                <AccordionItem value={`url-${index}`} key={index}>
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center">
                      <span>{result.lighthouseResult?.finalUrl || result.url}</span>
                      {result.lighthouseResult?.categories && (
                        <div className="ml-2 flex space-x-1">
                          {Object.values(result.lighthouseResult.categories).map((category) => (
                            <Badge 
                              key={category.id} 
                              variant={category.score >= 0.9 ? "default" : category.score >= 0.5 ? "outline" : "destructive"}
                              className="text-xs"
                            >
                              {Math.round(category.score * 100)}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <Card className="border-none shadow-none">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle>Performance Overview</CardTitle>
                          <Button 
                            variant="outline" 
                            size="sm"
                            asChild
                          >
                            <a 
                              href={`https://developers.google.com/speed/pagespeed/insights/?url=${encodeURIComponent(result.url)}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                            >
                              View Full Report <ExternalLink className="ml-2 h-4 w-4" />
                            </a>
                          </Button>
                        </div>
                        <CardDescription>
                          {result.lighthouseResult?.finalUrl || result.url}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                          {result.lighthouseResult?.categories && Object.values(result.lighthouseResult.categories).map((category) => (
                            <Gauge 
                              key={category.id}
                              title={category.title}
                              score={Math.round(category.score * 100)}
                              description={category.description}
                            />
                          ))}
                        </div>

                        {result.lighthouseResult?.audits && (
                          <div className="mt-6">
                            <h3 className="text-lg font-medium mb-4">Core Web Vitals</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {[
                                { id: 'first-contentful-paint', label: 'First Contentful Paint (FCP)', description: 'Time when the browser renders the first bit of content' },
                                { id: 'largest-contentful-paint', label: 'Largest Contentful Paint (LCP)', description: 'Time when the largest content element becomes visible' },
                                { id: 'total-blocking-time', label: 'Total Blocking Time (TBT)', description: 'Sum of all time periods that blocked user interactions' },
                                { id: 'cumulative-layout-shift', label: 'Cumulative Layout Shift (CLS)', description: 'Measures visual stability of the page' },
                                { id: 'speed-index', label: 'Speed Index', description: 'How quickly content is visually displayed during page load' },
                                { id: 'interactive', label: 'Time to Interactive (TTI)', description: 'Time it takes for the page to become fully interactive' },
                              ].map((metric) => {
                                const audit = result.lighthouseResult?.audits[metric.id];
                                if (!audit) return null;
                                
                                return (
                                  <MetricCard
                                    key={metric.id}
                                    title={metric.label}
                                    score={audit.score}
                                    displayValue={audit.displayValue}
                                    description={metric.description}
                                  />
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}
      </motion.div>
    </DashboardLayout>
  );
}
